import React, { useMemo, useState, useEffect } from 'react';
import LayoutComponent from '../BaseScreen';
import { useData } from '../../Context/DataContext';
import { useAuth } from '../../Context/AuthContext';
import { MoreHorizontal, ChevronUp, ChevronDown } from 'react-feather';

const ExternalScreen = () => {
  const { externalData, externalMeta } = useData();
  const { currentProfile } = useAuth();

  // Check if user is an intake director (simplified view)
  const isIntakeDirector = currentProfile?.privileges === 'director' && currentProfile?.department === 'intake';

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [locFilter, setLocFilter] = useState<string>('All');
  const [payerFilter, setPayerFilter] = useState<string>('All');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [sortColumn, setSortColumn] = useState<string>('avgDailyRate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Set appropriate default sort for intake directors (they can't see avgDailyRate)
  useEffect(() => {
    if (isIntakeDirector && sortColumn === 'avgDailyRate') {
      setSortColumn('numPatients');
    }
  }, [isIntakeDirector]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleLocFilterChange = (value: string) => {
    setLocFilter(value);
    setPage(1);
  };

  const handlePayerFilterChange = (value: string) => {
    setPayerFilter(value);
    setPage(1);
  };

  // Calculate likelihood based on financial metrics (for directors)
  const calculateLikelihood = (row: any): { label: string; color: string; bgColor: string; borderColor: string; score: number } => {
    // Parse values
    const paidPercent = parseFloat(String(row.paidPercent || '0').replace(/%/g, '')) || 0;
    const avgDailyRate = parseFloat(String(row.avgDailyRate || '0').replace(/[$,]/g, '')) || 0;
    const numPatients = parseInt(row.numPatients) || 0;
    const avgUnits = parseFloat(String(row.avgUnits || '0').replace(/,/g, '')) || 0;

    // Score based on metrics (total 100 points)
    let score = 0;

    // Paid percent scoring (0-30 points)
    if (paidPercent >= 70) score += 30;
    else if (paidPercent >= 50) score += 22;
    else if (paidPercent >= 30) score += 15;
    else if (paidPercent >= 15) score += 8;

    // Daily rate scoring (0-30 points)
    if (avgDailyRate >= 800) score += 30;
    else if (avgDailyRate >= 500) score += 22;
    else if (avgDailyRate >= 300) score += 15;
    else if (avgDailyRate >= 150) score += 8;

    // Patient count scoring (0-20 points) - more data = more reliable
    if (numPatients >= 10) score += 20;
    else if (numPatients >= 5) score += 15;
    else if (numPatients >= 3) score += 10;
    else if (numPatients >= 1) score += 5;

    // Average units scoring (0-20 points) - higher units = longer stays = better
    if (avgUnits >= 25) score += 20;
    else if (avgUnits >= 18) score += 15;
    else if (avgUnits >= 12) score += 10;
    else if (avgUnits >= 7) score += 5;

    // Determine likelihood category
    if (score >= 75) return { label: 'Highly Likely', color: 'text-green-400', bgColor: 'bg-green-500/15', borderColor: 'border-green-500/30', score };
    if (score >= 50) return { label: 'Likely', color: 'text-blue-400', bgColor: 'bg-blue-500/15', borderColor: 'border-blue-500/30', score };
    if (score >= 25) return { label: 'Unlikely', color: 'text-yellow-400', bgColor: 'bg-yellow-500/15', borderColor: 'border-yellow-500/30', score };
    return { label: 'Very Unlikely', color: 'text-red-400', bgColor: 'bg-red-500/15', borderColor: 'border-red-500/30', score };
  };

  const allRows = useMemo(() => {
    if (!externalData) return [];
    return externalData.map((row: any) => {
      const likelihood = calculateLikelihood(row);
      return {
        ...row,
        payer: row.payer || 'Unknown',
        facilitiesReported: row.facilitiesReported || 1,
        likelihood,
        likelihoodScore: likelihood.score
      };
    });
  }, [externalData]);

  const locOptions = useMemo(() => {
    const locs = new Set<string>();
    locs.add('All');
    allRows.forEach((r: any) => {
      if (r.levelOfCare) locs.add(r.levelOfCare);
    });
    return Array.from(locs);
  }, [allRows]);

  const payerOptions = useMemo(() => {
    const payers = new Set<string>();
    payers.add('All');
    allRows.forEach((r: any) => {
      if (r.payer) payers.add(r.payer);
    });
    return Array.from(payers);
  }, [allRows]);

  const parseValue = (value: any): number | string => {
    if (value === null || value === undefined || value === '—') return -Infinity;
    // If it's already a number, return it
    if (typeof value === 'number') {
      return value;
    }
    const str = String(value);
    // Parse currency values like "$1,234.56"
    if (str.startsWith('$')) {
      return parseFloat(str.replace(/[$,]/g, '')) || 0;
    }
    // Parse percent values like "85.50%"
    if (str.endsWith('%')) {
      return parseFloat(str.replace(/%/g, '')) || 0;
    }
    // Parse date values like "12/23/2025"
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
      const [m, d, y] = str.split('/');
      return new Date(`${y}-${m}-${d}`).getTime();
    }
    // Check if string is a plain number
    const num = parseFloat(str);
    if (!isNaN(num)) {
      return num;
    }
    return str.toLowerCase();
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let rows = allRows.filter((r: any) => {
      if (locFilter !== 'All' && String(r.levelOfCare || '') !== locFilter) return false;
      if (payerFilter !== 'All' && String(r.payer || '') !== payerFilter) return false;
      if (!q) return true;
      const haystack = [r.prefix, r.levelOfCare, r.avgDailyRate, r.payer]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });

    // Apply sorting
    if (sortColumn) {
      rows = [...rows].sort((a, b) => {
        const aVal = parseValue(a[sortColumn]);
        const bVal = parseValue(b[sortColumn]);

        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return rows;
  }, [allRows, locFilter, payerFilter, searchTerm, sortColumn, sortDirection]);

  const stats = useMemo(() => {
    // Calculate average daily rate from filtered rows
    const rates = filteredRows
      .map((r: any) => {
        if (!r.avgDailyRate) return null;
        const str = String(r.avgDailyRate);
        return parseFloat(str.replace(/[$,]/g, '')) || null;
      })
      .filter((v: number | null): v is number => v !== null && v > 0);

    const avgRate = rates.length > 0
      ? rates.reduce((sum: number, v: number) => sum + v, 0) / rates.length
      : 0;

    return {
      totalPrefixes: filteredRows.length,
      facilityParticipants: externalMeta?.totalFacilities || 0,
      avgDailyRate: avgRate,
    };
  }, [filteredRows, externalMeta]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const pageRows = filteredRows.slice(startIdx, startIdx + pageSize);

  const exportCsv = () => {
    const headers = ['payer', 'prefix', 'levelOfCare', 'numPatients', 'avgUnits', 'avgCharged', 'chargeRange', 'paidPercent', 'avgDailyRate', 'lastPaid'];
    const escape = (v: any) => {
      const s = String(v ?? '');
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const lines = [
      headers.join(','),
      ...filteredRows.map((r: any) => headers.map((h) => escape(r[h])).join(',')),
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vob_rates_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <LayoutComponent
      header={null}
      content={
        <div className="w-full h-full px-4 pb-4 overflow-hidden flex flex-col">
          {/* Stats Cards */}
          <div className={`grid ${isIntakeDirector ? 'grid-cols-2' : 'grid-cols-3'} gap-4 mt-4`}>
            <div className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="text-xs text-gray-400">Showing Results</div>
              <div className="mt-1 text-2xl font-semibold text-white">{stats.totalPrefixes.toLocaleString()}</div>
              <div className="text-xs text-gray-500 mt-1">Prefix/payer combinations</div>
            </div>
            {!isIntakeDirector && (
              <div className="bg-dark-card border border-dark-border rounded-xl p-4">
                <div className="text-xs text-gray-400">Avg Daily Rate</div>
                <div className="mt-1 text-2xl font-semibold text-green-300">
                  ${stats.avgDailyRate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-gray-500 mt-1">Across filtered results</div>
              </div>
            )}
            <div className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="text-xs text-gray-400">Facility Participants</div>
              <div className="mt-1 text-2xl font-semibold text-white">{stats.facilityParticipants}</div>
              <div className="text-xs text-gray-500 mt-1">Contributing data</div>
            </div>
          </div>

          {/* Filters */}
          <div className="mt-4 bg-dark-card border border-dark-border rounded-xl p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              {/* Search */}
              <div className="flex items-center gap-2">
                <input
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="px-3 py-1.5 w-[200px] bg-dark-input border border-dark-border text-sm rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-accent-teal transition-colors"
                  placeholder="Search prefix, payer..."
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400 font-medium">Payer</span>
                <select
                  value={payerFilter}
                  onChange={(e) => handlePayerFilterChange(e.target.value)}
                  className="px-3 py-1.5 bg-dark-input border border-dark-border text-sm rounded-xl text-white focus:outline-none focus:border-accent-teal transition-colors cursor-pointer"
                >
                  {payerOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400 font-medium">Level of Care</span>
                <select
                  value={locFilter}
                  onChange={(e) => handleLocFilterChange(e.target.value)}
                  className="px-3 py-1.5 bg-dark-input border border-dark-border text-sm rounded-xl text-white focus:outline-none focus:border-accent-teal transition-colors cursor-pointer"
                >
                  {locOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setPage(1);
                  }}
                  className="px-3 py-1.5 bg-dark-input border border-dark-border text-sm rounded-xl text-white focus:outline-none focus:border-accent-teal transition-colors cursor-pointer"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              {!isIntakeDirector && (
                <button
                  onClick={exportCsv}
                  className="px-3 py-1.5 rounded-xl bg-accent-teal text-black text-sm font-semibold hover:opacity-90"
                >
                  Export
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="mt-4 bg-dark-card border border-dark-border rounded-xl overflow-hidden flex flex-col flex-1 min-h-0">
            <div className="overflow-auto flex-1">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-dark-card z-10">
                  <tr className="text-left text-gray-400 border-b border-dark-border">
                    {(isIntakeDirector ? [
                      { label: 'Payer', key: 'payer' },
                      { label: 'Prefix', key: 'prefix' },
                      { label: 'Level of Care', key: 'levelOfCare' },
                      { label: '# Patients', key: 'numPatients' },
                      { label: 'Likelihood', key: 'likelihoodScore' },
                      { label: 'Last Payment', key: 'lastPaid' },
                    ] : [
                      { label: 'Payer', key: 'payer' },
                      { label: 'Prefix', key: 'prefix' },
                      { label: 'Level of Care', key: 'levelOfCare' },
                      { label: '# Patients', key: 'numPatients' },
                      { label: 'Avg Units', key: 'avgUnits' },
                      { label: 'Median Charged', key: 'avgCharged' },
                      { label: 'Charge Range', key: 'chargeRange' },
                      { label: 'Median Daily Rate', key: 'avgDailyRate' },
                      { label: 'Paid %', key: 'paidPercent' },
                      { label: 'Likelihood', key: 'likelihoodScore' },
                      { label: 'Last Payment', key: 'lastPaid' },
                    ]).map((col: any) => (
                      <th
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        className="px-4 py-3 font-medium cursor-pointer hover:text-white transition-colors select-none"
                      >
                        <div className="flex items-center gap-1">
                          {col.label}
                          {sortColumn === col.key && (
                            sortDirection === 'asc'
                              ? <ChevronUp size={14} className="text-accent-teal" />
                              : <ChevronDown size={14} className="text-accent-teal" />
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="px-4 py-3 font-medium w-12" />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r: any, idx: number) => {
                    const key = `${r.payer}-${r.prefix}-${r.levelOfCare}-${idx}`;
                    return (
                      <tr key={key} className="border-b border-dark-border hover:bg-dark-card-hover">
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-1 rounded-full border text-xs bg-blue-500/15 text-blue-300 border-blue-500/30">
                            {r.payer || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-white font-medium">{r.prefix || '—'}</td>
                        <td className="px-4 py-3 text-gray-300">{r.levelOfCare || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full text-xs font-medium ${
                            (r.numPatients || 0) > 5
                              ? 'bg-green-500/15 text-green-300 border border-green-500/30'
                              : 'bg-gray-500/15 text-gray-300 border border-gray-500/30'
                          }`}>
                            {r.numPatients || '—'}
                          </span>
                        </td>
                        {isIntakeDirector ? (
                          <>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-medium ${r.likelihood.bgColor} ${r.likelihood.color} ${r.likelihood.borderColor}`}>
                                {r.likelihood.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-300">{r.lastPaid || '—'}</td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-3 text-gray-300">{r.avgUnits || '—'}</td>
                            <td className="px-4 py-3 text-gray-300">{r.avgCharged || '—'}</td>
                            <td className="px-4 py-3 text-gray-500 text-xs">{r.chargeRange || '—'}</td>
                            <td className="px-4 py-3 text-green-300 font-medium">{r.avgDailyRate || '—'}</td>
                            <td className="px-4 py-3 text-gray-300">{r.paidPercent || '—'}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-medium ${r.likelihood.bgColor} ${r.likelihood.color} ${r.likelihood.borderColor}`}>
                                {r.likelihood.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-300">{r.lastPaid || '—'}</td>
                          </>
                        )}
                        <td className="px-4 py-3">
                          <button
                            className="p-2 rounded-lg hover:bg-dark-input border border-transparent hover:border-dark-border"
                            aria-label="More"
                          >
                            <MoreHorizontal size={16} className="text-gray-400" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {pageRows.length === 0 && (
                    <tr>
                      <td colSpan={isIntakeDirector ? 7 : 12} className="px-4 py-10 text-center text-gray-400">
                        No results.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 flex items-center justify-between text-sm text-gray-400 border-t border-dark-border">
              <div>
                Showing {filteredRows.length === 0 ? 0 : startIdx + 1} to{' '}
                {Math.min(startIdx + pageSize, filteredRows.length)} of {filteredRows.length} entries
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1.5 rounded-lg border border-dark-border hover:bg-dark-input disabled:opacity-50"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                >
                  Previous
                </button>
                <div className="px-2 text-gray-300">
                  {safePage} / {totalPages}
                </div>
                <button
                  className="px-3 py-1.5 rounded-lg border border-dark-border hover:bg-dark-input disabled:opacity-50"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default ExternalScreen;
